import { useCallback, useRef, useState } from 'react';
import { ArrowRight, CheckCircle2, Mail, Send, ShieldCheck } from 'lucide-react';
import { profile } from '../../data/siteContent';
import { useApp } from '../../context/AppContext';
import { Reveal } from '../ui/Reveal';
import { TurnstileWidget } from '../ui/TurnstileWidget';

const initialForm = { name: '', email: '', subject: '', message: '', website: '' };
const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';

// Contact posts validated data to the Node API and always exposes a direct fallback.
export function Contact() {
  const { language, t } = useApp();
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState('idle');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileFailed, setTurnstileFailed] = useState(false);
  const [challengeVersion, setChallengeVersion] = useState(0);
  const [verificationActive, setVerificationActive] = useState(false);
  const formStartedAt = useRef(0);

  const handleTurnstileToken = useCallback((token) => {
    setTurnstileToken(token);
    if (token) setTurnstileFailed(false);
  }, []);

  const handleTurnstileError = useCallback(() => setTurnstileFailed(true), []);

  function updateField(event) {
    if (!formStartedAt.current) formStartedAt.current = Date.now();
    setForm((current) => ({ ...current, [event.target.name]: event.target.value }));
  }

  async function submitForm(event) {
    event.preventDefault();

    if (turnstileSiteKey && !turnstileToken) {
      setTurnstileFailed(true);
      return;
    }

    setStatus('sending');

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          language,
          turnstileToken,
          formStartedAt: formStartedAt.current,
        }),
      });

      if (!response.ok) {
        if (response.status === 403) setStatus('verification-error');
        else if (response.status === 429) setStatus('rate-error');
        else setStatus('error');

        setTurnstileToken('');
        setChallengeVersion((current) => current + 1);
        return;
      }

      setForm(initialForm);
      setStatus('success');
      setTurnstileToken('');
      setChallengeVersion((current) => current + 1);
      formStartedAt.current = 0;
    } catch {
      setStatus('error');
      setTurnstileToken('');
      setChallengeVersion((current) => current + 1);
    }
  }

  return (
    <section id="contact" className="contact-section section-pad">
      <div className="contact-orbit" aria-hidden="true" />
      <div className="page-shell contact-grid">
        <Reveal className="contact-copy">
          <p className="eyebrow eyebrow--light">{t.contact.kicker}</p>
          <h2 className="display-title">{t.contact.title}</h2>
          <p>{t.contact.intro}</p>
          <a className="contact-email" href={`mailto:${profile.email}`}>
            <Mail size={20} /><span><small>{t.contact.direct}</small>{profile.email}</span><ArrowRight />
          </a>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            className="contact-form"
            onSubmit={submitForm}
            onFocusCapture={() => setVerificationActive(true)}
          >
            <div className="form-row">
              <label>
                <span>01</span>{t.contact.name}
                <input name="name" value={form.name} onChange={updateField} minLength="2" maxLength="80" required autoComplete="name" />
              </label>
              <label>
                <span>02</span>{t.contact.email}
                <input name="email" type="email" value={form.email} onChange={updateField} maxLength="254" required autoComplete="email" />
              </label>
            </div>
            <label>
              <span>03</span>{t.contact.subject}
              <input name="subject" value={form.subject} onChange={updateField} minLength="3" maxLength="120" required />
            </label>
            <label>
              <span>04</span>{t.contact.message}
              <textarea name="message" value={form.message} onChange={updateField} minLength="20" maxLength="3000" rows="4" required />
            </label>
            <label className="honeypot" aria-hidden="true">
              Website<input name="website" value={form.website} onChange={updateField} tabIndex="-1" autoComplete="off" />
            </label>

            {turnstileSiteKey && (
              <div className="turnstile-field">
                <p><span>05</span>{t.contact.verification}</p>
                {verificationActive ? (
                  <TurnstileWidget
                    key={challengeVersion}
                    siteKey={turnstileSiteKey}
                    onTokenChange={handleTurnstileToken}
                    onError={handleTurnstileError}
                  />
                ) : (
                  <div className="turnstile-placeholder">
                    <ShieldCheck size={18} />
                    <span>{t.contact.verificationHint}</span>
                  </div>
                )}
                {turnstileFailed && <small>{t.contact.verificationError}</small>}
              </div>
            )}

            <div className="form-footer">
              <p><ShieldCheck size={15} />{t.contact.privacy}</p>
              <button
                className="button button--light"
                type="submit"
                disabled={status === 'sending' || Boolean(turnstileSiteKey && !turnstileToken)}
              >
                {status === 'sending' ? t.contact.sending : t.contact.send}
                {status === 'success' ? <CheckCircle2 size={17} /> : <Send size={17} />}
              </button>
            </div>

            <div className={`form-status form-status--${status}`} role="status" aria-live="polite">
              {status === 'success' && t.contact.success}
              {status === 'error' && <>{t.contact.error} <a href={`mailto:${profile.email}`}>{profile.email}</a></>}
              {status === 'verification-error' && t.contact.verificationRejected}
              {status === 'rate-error' && t.contact.rateLimitError}
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
